/**
 * Collapsible panel surfacing template body tags that don't line up with the
 * clause library: clause-family tags naming no clause+option (unknown),
 * clause-family tags that resolve to more than one clause+option (ambiguous),
 * and clauses no tag references (orphaned). Displayed in the ClauseManager when
 * an org template is selected.
 *
 * An unknown tag's clause ID is NOT inferred — a tag like clause_a_b_c has no
 * single answer — so "Add Clause" carries the clicked tag to the caller
 * (`onAddClause(tag)`) purely as context; the administrator still types the
 * intended ID against the tag shown here.
 * Uses brand-compliant colours (emerald instead of green) and typography.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight, AlertTriangle, Plus } from 'lucide-react';
import { Button } from '../common';

interface AmbiguousTag {
  tag: string;
  candidates: { clauseId: string; optionId: string }[];
}

interface UnusedTagsPanelProps {
  unknownTags: string[];
  ambiguousTags: AmbiguousTag[];
  orphanedClauseIds: string[];
  /** Called with the bare tag key the admin clicked "Add Clause" beside, so
   *  the create form can show it as context (never as an inferred clause ID). */
  onAddClause: (tag: string) => void;
}

export default function UnusedTagsPanel({
  unknownTags,
  ambiguousTags,
  orphanedClauseIds,
  onAddClause,
}: UnusedTagsPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const totalIssues = unknownTags.length + ambiguousTags.length + orphanedClauseIds.length;

  if (totalIssues === 0) {
    return (
      <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-4">
        <p className="text-body-sm text-emerald-700 dark:text-emerald-400">All template tags have matching clauses.</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center justify-between p-4 text-left"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <span className="text-body-sm font-medium text-amber-800 dark:text-amber-300">
            Tag Issues ({totalIssues})
          </span>
        </div>
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-amber-500" />
        ) : (
          <ChevronRight className="h-4 w-4 text-amber-500" />
        )}
      </button>

      {isExpanded && (
        <div className="border-t border-amber-200 dark:border-amber-800 p-4 space-y-4">
          {unknownTags.length > 0 && (
            <div>
              <h4 className="text-body-sm font-medium text-amber-800 dark:text-amber-300 mb-2">
                Unknown Template Tags ({unknownTags.length})
              </h4>
              <p className="text-caption text-amber-600 dark:text-amber-400 mb-2">
                These clause tags are in the Word template but name no clause and option in the
                library. Add the clause with the intended ID — it is not inferred from the tag.
              </p>
              <div className="space-y-2">
                {unknownTags.map((tag) => (
                  <div
                    key={tag}
                    className="flex items-center justify-between rounded-md bg-white dark:bg-slate-700 p-2 border border-amber-100 dark:border-amber-900/30"
                  >
                    <code className="text-body-sm text-slate-700 dark:text-slate-300">{tag}</code>
                    <Button size="sm" variant="secondary" onClick={() => onAddClause(tag)}>
                      <Plus className="h-3 w-3 mr-1" />
                      Add Clause
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {ambiguousTags.length > 0 && (
            <div>
              <h4 className="text-body-sm font-medium text-amber-800 dark:text-amber-300 mb-2">
                Ambiguous Template Tags ({ambiguousTags.length})
              </h4>
              <p className="text-caption text-amber-600 dark:text-amber-400 mb-2">
                These tags resolve to more than one clause and option, so generation cannot tell
                which is meant. Rename a clause or option so each tag is unique.
              </p>
              <div className="space-y-2">
                {ambiguousTags.map(({ tag, candidates }) => (
                  <div
                    key={tag}
                    className="rounded-md bg-white dark:bg-slate-700 p-2 border border-amber-100 dark:border-amber-900/30"
                  >
                    <code className="text-body-sm text-slate-700 dark:text-slate-300">{tag}</code>
                    <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
                      matches:{' '}
                      {candidates
                        .map((candidate) => `${candidate.clauseId} / ${candidate.optionId}`)
                        .join(', ')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {orphanedClauseIds.length > 0 && (
            <div>
              <h4 className="text-body-sm font-medium text-amber-800 dark:text-amber-300 mb-2">
                Orphaned Clauses ({orphanedClauseIds.length})
              </h4>
              <p className="text-caption text-amber-600 dark:text-amber-400 mb-2">
                These clauses exist in the library but have no matching tag in the Word template.
              </p>
              <div className="space-y-1">
                {orphanedClauseIds.map((id) => (
                  <div
                    key={id}
                    className="flex items-center rounded-md bg-white dark:bg-slate-700 p-2 border border-amber-100 dark:border-amber-900/30"
                  >
                    <AlertTriangle className="h-3 w-3 text-amber-400 mr-2 shrink-0" />
                    <code className="text-body-sm text-slate-700 dark:text-slate-300">{id}</code>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
